/**
 * User identification manager for B2M Tracker
 * Uses browser fingerprinting to maintain consistent user ID across browsers and sessions
 */
export default class UserID {
  constructor(storage) {
    this.storage = storage;
    this.id = null;
    this.fingerprintId = null;
    this.externalId = null;
    this.isReady = false;
    // True while `this.id` is a throwaway random id (set before a stored/shared
    // id is found or the fingerprint resolves). Drives id_status:'temporary'.
    this.idIsTemporary = true;

    // Restore externally-set user ID (from a previous session) if present
    this.externalId = this.storage.get('b2m_external_user_id') || null;

    // Generate a temporary ID immediately (sync) for early tracking
    this._initTemporaryId();
  }

  /**
   * Set a customer-supplied user ID (e.g. their internal user PK).
   * Persisted in localStorage so the same identity is used across sessions.
   * Pass null/empty to clear.
   */
  setExternalId(externalId) {
    if (!externalId) {
      this.externalId = null;
      this.storage.remove('b2m_external_user_id');
      return;
    }
    this.externalId = String(externalId);
    this.storage.set('b2m_external_user_id', this.externalId);
  }

  getExternalId() {
    return this.externalId;
  }

  /**
   * Re-read a persisted/shared identity from storage. Called once cross-subdomain
   * storage (cookieDomain) is active — the constructor runs before that, so on a
   * fresh subdomain it would otherwise miss the id the parent/apex domain already
   * wrote to the .parent cookie and mint a throwaway temp id. Adopting it here
   * means the very first events carry the correct shared user_id.
   */
  adoptStoredId() {
    const storedId = this.storage.getUserId();
    if (storedId) {
      this.id = storedId;
      this.fingerprintId = storedId;
      this.idIsTemporary = false;
    }
    const ext = this.storage.get('b2m_external_user_id');
    if (ext) this.externalId = ext;
  }
  
  /**
   * Initialize a temporary ID immediately (synchronous)
   * This allows tracking to start before fingerprinting completes
   */
  _initTemporaryId() {
    // First try to get stored ID
    const storedId = this.storage.getUserId();
    if (storedId) {
      this.id = storedId;
      this.fingerprintId = storedId;
      this.idIsTemporary = false;
    } else {
      // Generate a quick random ID as temporary
      this.id = this._generateQuickId();
      this.fingerprintId = this.id;
      this.idIsTemporary = true;
    }
  }

  /** True when the SENT user_id is a throwaway temp (no external id and id not
   *  yet resolved/adopted). Backend uses id_status:'temporary' to stitch. */
  isIdTemporary() {
    return !this.externalId && this.idIsTemporary;
  }
  
  /**
   * Generate a quick random ID (synchronous)
   */
  _generateQuickId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 10);
    return `${randomPart}${timestamp}`;
  }
  
  async init() {
    this.fingerprintId = await this.generateAdvancedFingerprint();
    this.id = this.fingerprintId;

    const storedId = this.storage.getUserId();
    if (storedId && storedId === this.fingerprintId) {
      this.id = storedId;
    } else {
      this.storage.setUserId(this.id);
    }

    this.idIsTemporary = false; // resolved to the stable device fingerprint
    this.isReady = true;
  }

  getId() {
    // External (customer-supplied) ID always wins — this is the identity
    // the customer's backend already knows about, so experiments and events
    // should be keyed on it whenever it is available.
    return this.externalId || this.id;
  }

  getFingerprintId() {
    return this.fingerprintId;
  }

  /**
   * Generate an enhanced browser fingerprint
   * Uses hardware + locale signals that stay stable across browsers on the same device.
   * Browser-specific signals (canvas, WebGL, audio, fonts, plugins) are intentionally
   * excluded so the same physical device produces the same id in Chrome, Firefox, etc.
   */
  generateAdvancedFingerprint = async () => {
    const intl = (() => {
      try { return Intl.DateTimeFormat().resolvedOptions(); } catch (e) { return {}; }
    })();
    const uaData = navigator.userAgentData;

    const components = [
      // Display
      screen.width + 'x' + screen.height,
      screen.availWidth + 'x' + screen.availHeight,
      screen.colorDepth,
      screen.pixelDepth,
      window.devicePixelRatio,

      // Hardware
      navigator.hardwareConcurrency,
      navigator.maxTouchPoints,

      // Locale (IANA timezone — DST-stable, unlike getTimezoneOffset())
      intl.timeZone,
      intl.locale,
      (navigator.languages && navigator.languages.join(',')) || navigator.language,

      // Modern UA hints (Chromium); undefined in Firefox/Safari and dropped by the filter
      uaData && uaData.platform,
      uaData && typeof uaData.mobile === 'boolean' ? String(uaData.mobile) : undefined,
    ].filter(v => v !== undefined && v !== null && v !== '');

    return await this.hashString(components.join('||'));
  }

  /**
   * Hash string using crypto API or fallback
   */
  async hashString(str) {
    try {
      if (crypto.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
      }
    } catch (e) {
      // Fallback to simple hash
    }
    
    // Fallback simple hash for older browsers
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36).padStart(8, '0');
  }
} 

/**
 * Generate a hash from a string (legacy export for compatibility)
 */
export const hashString = (str) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return hash.toString(36)
}